import { Divider, Stack, StackProps } from "@mui/material";
import Card from "Components/card/Card";
import CountryFlag from "Components/CountryFlag";
import { PlanChip } from "Components/Plan";
import { StarRating } from "Components/rating";
import Row from "Components/Row";
import SmText, { SmTextMd } from "Components/text/SmText";
import { TextMd } from "Components/text/Text";
import { XsTextMd } from "Components/text/XsText";
import BuyerVerfifyTooltip from "Components/tooltip/BuyerVerifyTooltip";
import { COLORS } from "constants/layout";
import dayjs from "dayjs";
import useGetData from "hooks/useGetData";
import { useTranslation } from "hooks/useTranslation";
import { Fragment, memo, useMemo } from "react";
import { apiGetInquiries } from "services/api/inquiry";
import { InquiryBuyerType } from "types/buyer";

type Props = {
  farm_item?: string;
  farm_id?: string;
} & StackProps;

const Inquiries = ({ farm_id, farm_item, ...rest }: Props) => {
  const t = useTranslation();

  const { data } = useGetData({
    promise: () =>
      farm_id || farm_item ? apiGetInquiries({ farm_item, farm_id }) : null,
    deps: [farm_item, farm_id],
  });

  const inquiries: InquiryBuyerType[] = useMemo(
    () => data?.data?.data || [],
    [data]
  );

  if (inquiries.length === 0) return null;

  return (
    <Card {...rest}>
      <TextMd>
        {t("buyers_sent_inquiries", { number: data?.data?.count || 0 })}
      </TextMd>

      <Stack borderRadius={3} border={`1px solid ${COLORS.gray[200]}`}>
        {inquiries.map((inquiry, i) => {
          const {
            company_name,
            company_country,
            country_code,
            trust_star = 0,
            is_biz_verified,
            is_verified_work_email,
            plan_name,
            creation,
          } = inquiry;

          return (
            <Fragment key={i}>
              <Row
                justifyContent="space-between"
                py={{ xs: 0.75, md: 1.5 }}
                px={{ xs: 1, md: 2 }}
              >
                <Stack spacing={0.5}>
                  <Row spacing={0.5}>
                    <SmTextMd>{company_name}</SmTextMd>
                    <StarRating
                      readOnly
                      value={trust_star}
                      sx={{ ".MuiRating-icon svg": { width: 14, height: 13 } }}
                    />
                    {trust_star > 0 && (
                      <XsTextMd color="#FFCB45">
                        ({trust_star.toFixed(1)})
                      </XsTextMd>
                    )}
                  </Row>

                  <Row spacing={0.5}>
                    <PlanChip plan={plan_name} />
                    <BuyerVerfifyTooltip
                      data={{ is_biz_verified, is_verified_work_email }}
                    />
                    <CountryFlag
                      fd_country={company_country}
                      {...{ country_code }}
                    />
                  </Row>
                </Stack>

                <SmText color={COLORS.gray[500]}>
                  {dayjs(creation).format("D MMMM YYYY")}
                </SmText>
              </Row>

              {i < inquiries.length - 1 && <Divider />}
            </Fragment>
          );
        })}
      </Stack>
    </Card>
  );
};

export default memo(Inquiries);
